// src/layout/AppLayout.tsx
import { Container, Toolbar, Typography, Box } from "@mui/material";
import { styled } from "@mui/material/styles";

/* ================= Page container ================= */

export const AppPage = styled(Container)(({ theme }) => ({
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(0),

    // Slightly tighter on mobile
    [theme.breakpoints.down("sm")]: {
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
}));

/* ================= Card (responsive) ================= */

export const AppCard = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    backgroundColor:
        theme.palette.mode === "light"
            ? theme.palette.grey[50]
            : theme.palette.grey[900],
    border: `1px solid ${theme.palette.mode === "light"
            ? theme.palette.grey[300]
            : theme.palette.grey[700]
        }`,
    transition: "border-color 0.25s ease, background-color 0.25s ease",

    "&:hover": {
        borderColor: theme.palette.primary.light,
        backgroundColor:
            theme.palette.mode === "light"
                ? theme.palette.grey[100]
                : theme.palette.grey[800],
    },

    /* -------- MOBILE: look like a plain list -------- */
    [theme.breakpoints.down("sm")]: {
        padding: 0,
        border: "none",
        borderRadius: 0,
        backgroundColor: "transparent",

        "&:hover": {
            backgroundColor: "transparent",
            borderColor: "transparent",
        },
    },
}));

/* ================= List-style card ================= */

export const AppListCard = styled(Box)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius,
    backgroundColor:
        theme.palette.mode === "light"
            ? theme.palette.grey[50]
            : theme.palette.grey[900],
    border: `1px solid ${theme.palette.mode === "light"
            ? theme.palette.grey[300]
            : theme.palette.grey[700]
        }`,
    overflow: "hidden",
    transition: "border-color 0.25s ease",

    "&:hover": {
        borderColor: theme.palette.primary.light,
    },

    // Also flatten list cards on mobile
    [theme.breakpoints.down("sm")]: {
        border: "none",
        borderRadius: 0,
        backgroundColor: "transparent",
    },
}));

/* ================= Scroll area ================= */

export const AppScrollArea = styled("div")({
    maxHeight: 500,
    overflowY: "auto",
});

/* ================= Headers ================= */

export function AppHeader({
    title,
    subtitle,
}: {
    title: string;
    subtitle?: string;
}) {
    return (
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            <Typography
                variant="h4"
                fontWeight={600}
                sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}
            >
                {title}
            </Typography>

            {subtitle && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                >
                    {subtitle}
                </Typography>
            )}
        </Box>
    );
}

export function AppListHeader({ title }: { title: string }) {
    return (
        <Toolbar sx={{ px: { xs: 1, sm: 3 }, py: 2 }}>
            <Typography variant="h6" fontWeight={600}>
                {title}
            </Typography>
        </Toolbar>
    );
}
